import { Field, Form, Formik, FormikHelpers } from 'formik'
import moment from 'moment'
import { FC, ReactElement, useMemo, useState } from 'react'
import * as yup from 'yup'
import Button from '../../components/button'
import H3 from '../../components/h3'
import H4 from '../../components/h4'
import Input from '../../components/input'
import { trialDate } from '../../components/loggedNavigation'
import Modal from '../../components/modal'
import SelectInput from '../../components/selectInput'
import UploadAvatar from '../../components/uploadAvatar'
import { useAuth } from '../../hooks/auth'
import occupationList from '../../utils/occupationList'
import { RoutesEnum } from '../../utils/routesEnum'
import Default from '../loggedArea/default'
import { Buttons, UploadAvatarContainer } from './styles'

const schema = yup.object().shape({
  name: yup.string().min(3, 'Nome inválido')
    .matches(/[A-z]*\s[A-z]{1,}/, 'Nome e sobrenome são obrigatórios')
    .required('Nome obrigatório'),
  phone: yup.string().min(11, 'Celular inválido'),
  birthDate: yup.string().min(10, 'Data de nascimento inválida')
    .required('Data de nascimento é obrigatório')
})

const Profile: FC = (): ReactElement => {
  const [isOpen, setOpen] = useState(false)
  const [avatarFile, setFileAvatar] = useState<File>()
  const { user, updateUser } = useAuth()

  const trial = trialDate(user)

  const avatarToUpload = useMemo(() => {
    if (avatarFile) return window.URL.createObjectURL(avatarFile)
  }, [avatarFile])

  const initialValues = useMemo(() => ({
    name: user?.name,
    birthDate: user?.birthDate ? moment(user?.birthDate).format('DD/MM/YYYY') : undefined,
    phone: user?.phone,
    occupation: user?.occupation
  }), [user])

  const submit = async (
    values: typeof initialValues,
    helpers: FormikHelpers<typeof initialValues>
  ): Promise<void> => {
    helpers.setSubmitting(true)
    try {
      await updateUser({
        ...values,
        avatar: avatarFile,
        birthDate: values.birthDate ? moment(values.birthDate, 'DD/MM/YYYY').toDate() : undefined
      })
      setOpen(true)
    } catch (err) { console.log(err) }
    helpers.setSubmitting(false)
  }

  return (
    <Default
      title='Perfil'
      subtitle='Aqui você consegue alterar as informações sobre o seu perfil'
      breadcrumbs={[
        { title: 'Perfil', to: RoutesEnum.PROFILE },
        { title: 'Meu perfil', to: RoutesEnum.PROFILE }
      ]}
    >
      <H3>Meu perfil</H3>
      <Formik
        validationSchema={schema}
        initialValues={initialValues}
        enableReinitialize
        onSubmit={submit}
      >
        {({ isSubmitting, isValid }) => (
          <Form>
            <UploadAvatarContainer>
              <UploadAvatar
                avatar={avatarToUpload || user?.avatar || undefined}
                onChange={e => setFileAvatar(e.target.files?.[0])}
              />
              <div className='user'>
                <H4>{user?.name}</H4>
                <p>{user?.occupation}</p>
                {trial && <span>{trial} dias de acesso restante</span>}
              </div>
            </UploadAvatarContainer>
            <Field
              component={Input}
              name='name'
              label='Nome'
            />
            <Field
              component={Input}
              mask='99/99/9999'
              name='birthDate'
              label='Data de nascimento'
              type='tel'
            />
            <Field
              component={Input}
              name='phone'
              label='Celular'
              mask='(99) 99999-9999'
              type='tel'
            />
            <Field
              component={SelectInput}
              name='occupation'
              label='Profissão'
              options={occupationList.map(occupation => ({ label: occupation, value: occupation }))}
            />
            <Buttons>
              <Button
                isLoading={isSubmitting}
                block
                disabled={!isValid}
                type='submit'
              >
                ALTERAR CADASTRO
              </Button>
            </Buttons>
          </Form>
        )}
      </Formik>
      {isOpen && (
        <Modal
          title='Sucesso'
          okButtonText='OK'
          onOk={() => {
            setOpen(false)
          }}
        >
          Perfil atualizado com sucesso
        </Modal>
      )}
    </Default>
  )
}

export default Profile
